import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";

/**
 * Public page reached via the "Annulla cancellazione" email link
 * (URL: /gdpr/cancel-deletion?token=...). No auth required — the
 * token IS the authentication.
 */
export default function GdprCancelDeletion() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [status, setStatus] = useState("confirming"); // confirming | ok | error
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (!token) { setStatus("error"); setErrorMsg("Token mancante nell'URL."); return; }
    let cancelled = false;
    (async () => {
      try {
        await api.gdprCancelDeletion(token);
        if (!cancelled) setStatus("ok");
      } catch (e) {
        if (!cancelled) {
          setStatus("error");
          setErrorMsg(e?.response?.data?.detail || "Impossibile annullare la richiesta.");
        }
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  return (
    <div className="min-h-dvh pxos-gradient-surface grid place-items-center p-4"
         data-testid="gdpr-cancel-deletion-page">
      <div className="pxos-card max-w-md w-full p-6 sm:p-7 space-y-4 text-center">
        {status === "confirming" && (
          <>
            <div className="mx-auto rounded-full bg-neutral-100 p-3 w-fit">
              <Loader2 size={26} className="animate-spin opacity-70" />
            </div>
            <h1 className="font-display text-xl font-semibold">Annullamento in corso…</h1>
            <p className="text-sm opacity-70">Verifica del token e ripristino dell'account.</p>
          </>
        )}
        {status === "ok" && (
          <>
            <div className="mx-auto rounded-full bg-emerald-100 p-3 w-fit">
              <CheckCircle2 size={26} className="text-emerald-700" />
            </div>
            <h1 className="font-display text-xl font-semibold" data-testid="gdpr-cancel-ok-title">
              Cancellazione annullata
            </h1>
            <p className="text-sm opacity-80">
              La richiesta di eliminazione dell'account è stata annullata. 
              Puoi tornare ad accedere normalmente.
            </p>
            <Link to="/login" className="pxos-btn-primary inline-flex items-center gap-2 text-sm mt-2"
                  data-testid="gdpr-cancel-login-link">
              <ShieldCheck size={14} /> Vai al login
            </Link>
          </>
        )}
        {status === "error" && (
          <>
            <div className="mx-auto rounded-full bg-red-100 p-3 w-fit">
              <XCircle size={26} className="text-red-700" />
            </div>
            <h1 className="font-display text-xl font-semibold" data-testid="gdpr-cancel-error-title">
              Non è stato possibile annullare
            </h1>
            <p className="text-sm opacity-80" data-testid="gdpr-cancel-error-detail">
              {errorMsg || "Il token non è valido o la cancellazione è già stata completata."}
            </p>
            <Link to="/" className="text-xs underline opacity-70">Torna alla home</Link>
          </>
        )}
      </div>
    </div>
  );
}
